import { useContext, useState } from 'react';
import { ExtraBase, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ItemExtrasAutoApplier } from '../../../lib/helpers/ItemExtrasAutoApplier';
import { ApplierOption } from '../../../lib/helpers/ApplierOption';
import { ExtrasHaveChanged } from '../../../lib/helpers/SmartSendSocketHelper';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { SaveItemsAction } from '../../../lib/context/SmartSendContextModels';
import { ProtectionSelection } from '../ProtectionSelection';
import { ProtectionExtraIds } from '../../ExtrasHelper';
import { P2GBox, P2GButton } from '@parcel2go/core-ui';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { ToSmartSendAnalyticsModel, ToSmartSendAnalyticsPatchModel } from '../quotes/utils/CurrentSelectionType';

interface props {
  item: SmartSendItemApiModel;
}

export default function ItemProtectionSummaryDrop(props: props) {
  const ctx = useContext(SmartSendContext);
  const { getQuoteOptions } = useSmartSendApi();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { track } = useSmartSendAnalytics();
  const [touched, setTouched] = useState(false);

  const [item, setItem] = useState<SmartSendItemApiModel>(
    ItemExtrasAutoApplier(JSON.parse(JSON.stringify({ ...props.item })))
  );

  const getQuote = async () => {
    if (!item.Service) {
      return null;
    }

    const r = await getQuoteOptions(
      item.Service?.Provider,
      item.Service?.AccountId,
      ItemExtrasAutoApplier(item),
      item.Service?.Id
    );

    return r.success ? r.result.Quotes[0] : null;
  };

  const onExtrasSave = (extras: ExtraBase[]) => {
    setItem({ ...item, Extras: extras });
  };

  const onSave = () => {
    if (ExtrasHaveChanged(props.item.Extras, item.Extras)) {
      const actions: ApplierOption[] = [];
      actions.push('Extras');

      const patchModel = ToSmartSendAnalyticsPatchModel({ Extras: item.Extras.map((e) => e.Id) }, actions);
      track({
        type: 'item_edited',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem]),
          surface: 'item-protection-summary-drop',
          action: 'save-item-edits',
          changedFields: actions,
          itemScope: 'single',
          extraIds: item.Extras.map((e) => e.Id),
          serviceId: item.Service?.Id ?? null,
          serviceProvider: item.Service?.Provider ?? null,
          originalItems: [ToSmartSendAnalyticsModel(props.item)],
          patchModel: patchModel
        }
      });

      ctx.dispatch(
        new SaveItemsAction([item.Id], actions, {
          Extras: item.Extras
        })
      );
    }
  };

  return (
    <P2GBox sx={{ display: 'flex', gap: 2, flexDirection: 'column', py: 1 }} onClick={() => setTouched(true)}>
      <P2GBox p={2}>
        <ProtectionSelection
          currentCoverPercentage={item.State?.validation?.QuoteState?.Quote?.Options[0]?.CoverPercentage || 0}
          getQuote={getQuote}
          item={props.item}
          includeServiceModel={false}
          currentSelection={item.Extras}
          onUpdate={(q, o) => {
            const _extras = item.Extras.filter((x) => !ProtectionExtraIds.includes(x.Id));
            o?.Extras.forEach((e) => {
              if (!_extras.find((x) => x.Id === e.Id)) {
                _extras.push(e);
              }
            });
            onExtrasSave(_extras);
          }}
        />
      </P2GBox>
      {touched && (
        <P2GBox>
          <P2GButton onClick={onSave} colour="primary">
            {t('SAVE_BUTTON', { defaultValue: 'Save' })}
          </P2GButton>
        </P2GBox>
      )}
    </P2GBox>
  );
}
